import type { WithContext, Product as SchemaOrgProduct, ItemList } from 'schema-dts';
import type { Product, ProductCategory, Listing } from 'shared/@generated/models';
import type { ShopSettings } from 'shared/state/shop';
import { getForSalePets } from 'shared/library/pet-roles';
import { isPetAvailable } from 'shared/library/pet-status';

export type ProductSchemaOptions = {
  url?: string;
  shopSettings?: ShopSettings | null;
};

export type CategoryListItemInput = {
  position: number;
  name?: string;
  url?: string;
};

export type CategorySchemaInput = {
  url?: string;
  numberOfItems?: number;
  items?: CategoryListItemInput[];
};

type RatedProduct = Product & {
  rating_score?: number | null;
  rating_votes?: number | null;
};

const DEFAULT_PRODUCT_SHIPPING_COUNTRY = 'SE';
const DEFAULT_PRODUCT_RETURN_WINDOW_DAYS = 30;
const DEFAULT_PRICE_VALIDITY_DAYS = 365;
const SKU_MAX_LENGTH = 70;

const clampRating = (rating: number) => Number(Math.max(1, Math.min(5, rating)).toFixed(1));

const nonEmptyStrings = (values: Array<string | null | undefined>) =>
  values.filter((value): value is string => Boolean(value));

const formatDate = (date: Date) => date.toISOString().slice(0, 10);

const defaultPriceValidUntil = () => {
  const validUntil = new Date();
  validUntil.setUTCDate(validUntil.getUTCDate() + DEFAULT_PRICE_VALIDITY_DAYS);

  return formatDate(validUntil);
};

const sanitizeSku = (sku?: string | null) => {
  if (!sku) {
    return undefined;
  }

  const normalizedSku = sku
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, SKU_MAX_LENGTH);

  return normalizedSku.length > 0 ? normalizedSku : undefined;
};

const shippingFeeAmount = (shopSettings?: ShopSettings | null) => {
  const shippingFeeMinor = shopSettings?.shipping_fee_minor;

  if (typeof shippingFeeMinor !== 'number' || !Number.isFinite(shippingFeeMinor)) {
    return undefined;
  }

  return shippingFeeMinor / 100;
};

const shippingTransitDays = (
  variant: Product['product_variants'][number],
  shopSettings?: ShopSettings | null,
) => {
  const minDays =
    variant.available && variant.in_stock > 0
      ? (shopSettings?.shipping_days_min ?? 1)
      : (shopSettings?.shipping_days_slow_min ?? 3);
  const maxDays =
    variant.available && variant.in_stock > 0
      ? (shopSettings?.shipping_days_max ?? 5)
      : (shopSettings?.shipping_days_slow_max ?? 10);

  return {
    min: Math.max(0, Math.min(minDays, maxDays)),
    max: Math.max(minDays, maxDays),
  };
};

export const buildProductSchema = ({
  product,
  url,
  fallbackImage,
  shopSettings,
}: {
  product: Product;
  url: string;
  fallbackImage: string;
  shopSettings?: ShopSettings | null;
}): WithContext<SchemaOrgProduct> => {
  const productPriceValidUntil = defaultPriceValidUntil();
  const configuredShippingFeeAmount = shippingFeeAmount(shopSettings);
  const productImageUrls = nonEmptyStrings([
    product.avatar?.url,
    ...product.media_assets.map((asset) => asset.url),
  ]);
  const primaryVariant = product.product_variants[0];
  const ratedProduct = product as RatedProduct;
  const parsedAggregateRating =
    typeof ratedProduct.rating_score === 'number' &&
    Number.isFinite(ratedProduct.rating_score) &&
    typeof ratedProduct.rating_votes === 'number' &&
    Number.isFinite(ratedProduct.rating_votes) &&
    ratedProduct.rating_votes > 0
      ? {
          ratingValue: clampRating(ratedProduct.rating_score),
          reviewCount: Math.max(1, Math.round(ratedProduct.rating_votes)),
        }
      : undefined;
  const aggregateRating = parsedAggregateRating
    ? {
        '@type': 'AggregateRating' as const,
        ratingValue: parsedAggregateRating.ratingValue,
        reviewCount: parsedAggregateRating.reviewCount,
      }
    : undefined;

  const offers: SchemaOrgProduct['offers'] = product.product_variants.map((variant) => {
    const transitDays = shippingTransitDays(variant, shopSettings);
    const variantCurrency = variant.pricing.local.total.currency;

    return {
      '@type': 'Offer' as const,
      name: variant.name,
      sku: sanitizeSku(variant.sku),
      gtin: variant.ean || undefined,
      price: variant.pricing.local.total.amount,
      priceCurrency: variantCurrency,
      priceValidUntil: productPriceValidUntil,
      availability:
        variant.available && variant.in_stock > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      shippingDetails: {
        '@type': 'OfferShippingDetails' as const,
        shippingDestination: {
          '@type': 'DefinedRegion' as const,
          addressCountry: DEFAULT_PRODUCT_SHIPPING_COUNTRY,
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime' as const,
          handlingTime: {
            '@type': 'QuantitativeValue' as const,
            minValue: 0,
            maxValue: 1,
            unitCode: 'd',
          },
          transitTime: {
            '@type': 'QuantitativeValue' as const,
            minValue: transitDays.min,
            maxValue: transitDays.max,
            unitCode: 'd',
          },
        },
        shippingRate:
          configuredShippingFeeAmount !== undefined && variantCurrency
            ? {
                '@type': 'MonetaryAmount' as const,
                value: configuredShippingFeeAmount,
                currency: variantCurrency,
              }
            : undefined,
      },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy' as const,
        applicableCountry: DEFAULT_PRODUCT_SHIPPING_COUNTRY,
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: DEFAULT_PRODUCT_RETURN_WINDOW_DAYS,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
        merchantReturnLink: 'https://getapet.com/purchase-terms',
      },
      url,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.summary || product.description,
    image: productImageUrls.length > 0 ? productImageUrls : [fallbackImage],
    url,
    sku: sanitizeSku(primaryVariant?.sku),
    gtin: primaryVariant?.ean || undefined,
    category: product.product_categories.map((category) => category.name).join(', ') || undefined,
    brand: product.brand?.name
      ? {
          '@type': 'Brand',
          name: product.brand.name,
        }
      : undefined,
    aggregateRating,
    offers: offers.length > 0 ? offers : undefined,
  };
};

export const buildListingSchema = ({
  listing,
  url,
  fallbackImage,
}: {
  listing: Listing;
  url: string;
  fallbackImage: string;
}): WithContext<SchemaOrgProduct> => {
  const forSalePets = getForSalePets(listing.pets);
  const listingImageUrls = nonEmptyStrings([
    listing.avatar?.url,
    ...listing.media_assets.map((asset) => asset.url),
    ...forSalePets.map((pet) => pet.avatar?.url),
  ]);
  const hasAvailablePets = !listing.deleted_at && forSalePets.some((pet) => isPetAvailable(pet));
  const sellerName = listing.user.display_name || listing.user.name || undefined;
  const listingCurrency = listing.currency || undefined;

  const listingOffer: SchemaOrgProduct['offers'] =
    listing.price !== null && listingCurrency
      ? {
          '@type': 'Offer',
          price: listing.price,
          priceCurrency: listingCurrency,
          availability: hasAvailablePets
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url,
          seller: sellerName
            ? {
                '@type': listing.user.is_breeder ? 'Organization' : 'Person',
                name: sellerName,
              }
            : undefined,
        }
      : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: listing.title || listing.breed_name,
    description: listing.description || listing.short_description || undefined,
    image: listingImageUrls.length > 0 ? listingImageUrls : [fallbackImage],
    url,
    sku: sanitizeSku(listing.slug),
    category:
      [listing.animal, listing.breed_type, listing.breed_name].filter(Boolean).join(', ') ||
      undefined,
    offers: listingOffer,
  };
};

export const buildCategorySchema = ({
  category,
  url,
  numberOfItems,
  items,
}: {
  category: ProductCategory;
  url: string;
  numberOfItems?: number;
  items?: CategoryListItemInput[];
}): WithContext<ItemList> => {
  const fallbackItems: CategoryListItemInput[] = (category.products ?? []).map(
    (product, index) => ({
      position: index + 1,
      name: product.name,
    }),
  );
  const itemList = items ?? fallbackItems;

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: category.name,
    description: category.summary,
    image: category.avatar?.url || undefined,
    url,
    numberOfItems:
      numberOfItems ??
      category.products_count ??
      (itemList.length > 0 ? itemList.length : undefined),
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: itemList.map((item) => ({
      '@type': 'ListItem' as const,
      position: item.position,
      name: item.name,
      url: item.url,
    })),
  };
};
